import {useState} from 'react';
import {Calendar as CalendarIcon, Clock} from 'lucide-react';
import {parseISO, format} from 'date-fns';
import {cn} from '@lib/utils';
import {Button} from '@ui/shadcn/button';
import {Calendar} from '@ui/shadcn/calendar';
import {Popover, PopoverContent, PopoverTrigger} from '@ui/shadcn/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/shadcn/select';

const HOURS = Array.from({length: 24}, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({length: 12}, (_, i) =>
  String(i * 5).padStart(2, '0'),
);

interface DateTimeFieldProps {
  /** Дата `YYYY-MM-DD` (пусто — не выбрана). */
  date: string;
  /** Время `HH:mm` (пусто — без времени). */
  time: string;
  /** Новая дата в `YYYY-MM-DD`. */
  onDateChange: (date: string) => void;
  /** Новое время в `HH:mm`. */
  onTimeChange: (time: string) => void;
  /** Минимально допустимая дата `YYYY-MM-DD` (раньше неё — задизейблено). */
  minDate?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** Текст на кнопке, когда дата не выбрана. */
  placeholder?: string;
  /** Доп. классы на кнопку-триггер (напр. цвет «просрочено»). */
  triggerClassName?: string;
  className?: string;
  /** z-index содержимого поповера. Для рендера поверх модалок передать
   *  соответствующий слой (напр. `'calc(var(--z-modal-task) + 1)'`). */
  contentZIndex?: string;
}

/**
 * Единый виджет «дата + время»: одна кнопка-триггер, а в поповере — Calendar и
 * выбор часов/минут. Время выбираем через Select (ЧЧ/ММ), а не `input[type=time]`:
 * нативный пикер на мобильном улетает за экран и не стилизуется (та же причина,
 * по которой дата — Calendar-Popover, а не `input[type=date]`).
 */
export const DateTimeField = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  minDate,
  invalid,
  disabled,
  placeholder = 'Выберите дату',
  triggerClassName,
  className,
  contentZIndex = 'var(--z-dropdown)',
}: DateTimeFieldProps) => {
  const [open, setOpen] = useState(false);
  const [hour, minute] = time ? time.split(':') : ['', ''];

  const label = date
    ? `${parseISO(date).toLocaleDateString('ru-RU')}${time ? `, ${time}` : ''}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          type='button'
          variant='outline'
          aria-invalid={invalid}
          className={cn(
            'w-full justify-start font-normal text-[var(--color-foreground)] field-trigger',
            triggerClassName,
            className,
          )}
        >
          <CalendarIcon size={13} />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-auto p-0'
        align='start'
        style={{zIndex: contentZIndex}}
      >
        <Calendar
          mode='single'
          selected={date ? parseISO(date) : undefined}
          onSelect={(d) => {
            if (!d) return;
            onDateChange(format(d, 'yyyy-MM-dd'));
          }}
          disabled={minDate ? {before: parseISO(minDate)} : undefined}
          autoFocus
        />
        {/* Время — под календарём, чтобы дата и время выбирались в одном поповере */}
        <div className='flex items-center gap-2 border-t border-[var(--color-border)] p-2'>
          <Clock
            size={13}
            className='shrink-0 text-[var(--color-muted-foreground)]'
          />
          <Select
            value={hour}
            onValueChange={(h) => onTimeChange(`${h}:${minute || '00'}`)}
          >
            <SelectTrigger size='sm' className='w-[68px]'>
              <SelectValue placeholder='ЧЧ' />
            </SelectTrigger>
            <SelectContent position='popper' style={{zIndex: contentZIndex}}>
              {HOURS.map((h) => (
                <SelectItem key={h} value={h}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className='text-[var(--color-muted-foreground)]'>:</span>
          <Select
            value={minute}
            onValueChange={(m) => onTimeChange(`${hour || '00'}:${m}`)}
          >
            <SelectTrigger size='sm' className='w-[68px]'>
              <SelectValue placeholder='ММ' />
            </SelectTrigger>
            <SelectContent position='popper' style={{zIndex: contentZIndex}}>
              {MINUTES.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
};
